import React, { useState, useEffect, useCallback } from 'react';

/**
 * AdminLogs — Activity log viewer with filtering.
 *
 * Shows all platform transactions, admin actions, bets,
 * settlements, and corrections with timestamps.
 */
const AdminLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [limit, setLimit] = useState(100);

    const apiFetch = useCallback(async (path) => {
        const res = await fetch(`/api/admin${path}`, { credentials: 'include' });
        return res.json();
    }, []);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch(`/logs?type=${filter}&limit=${limit}`);
            setLogs(data.logs || []);
        } catch { /* ignore */ }
        setLoading(false);
    }, [apiFetch, filter, limit]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const typeColors = {
        deposit: 'text-emerald-400 bg-emerald-500/10',
        payout: 'text-blue-400 bg-blue-500/10',
        fee: 'text-amber-400 bg-amber-500/10',
        refund: 'text-purple-400 bg-purple-500/10',
    };

    return (
        <div className="rounded-lg border border-white/10 bg-white/[0.02]">
            <div className="flex items-center justify-between p-4 border-b border-white/5 flex-wrap gap-2">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-400">
                    Activity Logs ({logs.length})
                </h3>
                <div className="flex items-center gap-2">
                    {['all', 'deposit', 'payout', 'fee', 'refund'].map(t => (
                        <button
                            key={t}
                            onClick={() => setFilter(t)}
                            className={`text-xs px-2 py-1 rounded font-bold uppercase tracking-wider transition-all ${filter === t
                                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    : 'text-gray-500 hover:text-gray-300 border border-transparent'
                                }`}
                        >
                            {t}
                        </button>
                    ))}
                    <select
                        value={limit}
                        onChange={e => setLimit(parseInt(e.target.value))}
                        className="text-xs bg-white/[0.03] border border-white/10 rounded px-2 py-1 text-gray-400 outline-none"
                    >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={500}>500</option>
                    </select>
                    <button onClick={fetchLogs} className="text-xs text-blue-400 hover:text-blue-300">
                        Refresh
                    </button>
                </div>
            </div>

            {loading ? (
                <p className="text-xs text-gray-600 p-4">Loading logs...</p>
            ) : logs.length === 0 ? (
                <p className="text-xs text-gray-600 p-4">No activity yet.</p>
            ) : (
                <div className="max-h-[400px] overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-[#0a0a1a]">
                            <tr className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/5">
                                <th className="text-left px-4 py-2">Time</th>
                                <th className="text-left px-4 py-2">Type</th>
                                <th className="text-left px-4 py-2">User</th>
                                <th className="text-right px-4 py-2">Amount</th>
                                <th className="text-left px-4 py-2">Memo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log, i) => (
                                <tr key={log.id || i} className="border-b border-white/5 hover:bg-white/[0.02]">
                                    <td className="px-4 py-2 font-mono text-gray-500 whitespace-nowrap">
                                        {formatTime(log.created_at)}
                                    </td>
                                    <td className="px-4 py-2">
                                        <span className={`text-xs px-1.5 py-0.5 rounded font-bold uppercase ${typeColors[log.type] || 'text-gray-400 bg-white/5'}`}>
                                            {log.type}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-gray-300 font-mono max-w-[100px] truncate">
                                        {log.display_name || log.wallet_address?.slice(0, 8) || '—'}
                                    </td>
                                    <td className="px-4 py-2 text-right font-mono text-white">
                                        {parseFloat(log.amount_sol) > 0
                                            ? parseFloat(log.amount_sol).toFixed(4)
                                            : '—'
                                        }
                                    </td>
                                    <td className="px-4 py-2 text-gray-400 max-w-[250px] truncate" title={log.memo}>
                                        {log.memo || '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

function formatTime(ts) {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) +
        ' ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default AdminLogs;
